import 'feather-icons'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import Column from '../components/Column'
import { BasicLink } from '../components/Link'
import Row from '../components/Row'
import { useGlobalData } from '../contexts/GlobalData'
import { usePairs } from '../contexts/PairData'
import { useDarkModeManager } from '../contexts/LocalStorage'
import { TYPE } from '../Theme'
import LocalLoader from '../components/LocalLoader'
import { LoaderThin } from '../components/LoaderThin'
import { formatAmount } from '../utils/formatAmount'
import CryptoCard from '../components/CryptoCard'
import Header from '../components/Header'

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;
  width: 100%;
  max-width: 1264px;

  @media screen and (max-width: 1080px) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`

export const DarkPageContainer = styled.div`
  background: ${({ theme }) => theme.bg1};
  min-height: 100vh;
  padding: 120px 48px 80px;
  margin-top: 0;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  @media screen and (max-width: 768px) {
    padding: 60px 20px 80px;
  }
`

export const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 48px;
  width: 100%;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`

export const SectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 756px;
`

export const TVLDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-end;
  text-align: right;
`

export const ShowAllButton = styled.button`
  background: ${({ theme }) => theme.bg3};
  border: none;
  border-radius: 50px;
  padding: 12px 16px 12px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin: 48px 0 36px 0;
  transition: all 0.2s ease;

  :hover {
    transform: translateY(-1px);
  }
`

export const SectionDivider = styled.div`
  background: ${({ theme }) => theme.bg3};
  height: 1px;
  width: 100%;
  margin: 48px 0;
`

function AllPairsPage() {
  const [, toggleDarkMode] = useDarkModeManager()

  useEffect(() => {
    window.scrollTo(0, 0)
    // Force dark mode for this page
    toggleDarkMode(true)
  }, [toggleDarkMode])

  const { totalValueLockedSecurity, totalValueLockedCrypto } = useGlobalData()
  const { secPairs = {}, cryptoPairs = {} } = usePairs()

  const visibleSec = secPairs
  const visibleCrypto = cryptoPairs
  const showSec = Object.keys(visibleSec).length
  const showCrypto = Object.keys(visibleCrypto).length
  return (
    <>
      <Header />
      <DarkPageContainer>
        {/* Security Pools Section */}
        <SectionHeader>
          <SectionTitle>
            <TYPE.primary style={{ fontSize: '32px', fontWeight: 'bold', lineHeight: 1 }}>Security Pools</TYPE.primary>
            <TYPE.light
              style={{
                fontSize: '20px',
                opacity: 0.6,
                color: '#fff',
                lineHeight: '1.5',
                maxWidth: '756px',
                letterSpacing: '-0.2px',
              }}
            >
              Security Pools are decentralized financial mechanisms in the realm of cryptocurrencies designed to enhance
              the security and stability of digital asset transactions. These pools serve as a collective resource where
              participants contribute their funds to mitigate risks associated with various crypto-related activities.
            </TYPE.light>
          </SectionTitle>

          <TVLDisplay>
            <TYPE.light style={{ fontSize: '14px', lineHeight: '1.4' }}>Total Value locked</TYPE.light>
            {Boolean(showSec) ? (
              <TYPE.primary style={{ fontSize: '48px', fontWeight: 'bold', lineHeight: 1 }}>
                ${formatAmount(+totalValueLockedSecurity.toFixed(0))}
              </TYPE.primary>
            ) : (
              <LoaderThin size={32} />
            )}
          </TVLDisplay>
        </SectionHeader>

        {/* Security Pools Grid */}
        {Boolean(showSec) && (
          <Grid>
            {Object.keys(visibleSec)
              .slice(0, 3)
              .map((item, index) => (
                <CryptoCard key={`security-pool-${index}`} item={visibleSec[item]} allPairs={visibleSec} />
              ))}
          </Grid>
        )}

        {!showSec && (
          <Row>
            <Column style={{ alignItems: 'center', width: '100%' }}>
              <LocalLoader />
            </Column>
          </Row>
        )}

        {/* Show All Button for Security Pools */}
        <BasicLink to={`/pools/security`}>
          <ShowAllButton>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g opacity="0.5">
                <path
                  d="M6.66666 5H16.6667"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.33334 5.00834L3.34168 4.99908"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.33334 10.0083L3.34168 9.99908"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.33334 15.0083L3.34168 14.9991"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66666 10H16.6667"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66666 15H16.6667"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>

            <TYPE.light style={{ fontSize: '16px', lineHeight: '1.4' }}>Show All</TYPE.light>
          </ShowAllButton>
        </BasicLink>

        <SectionDivider />

        {/* Crypto Pools Section */}
        <SectionHeader>
          <SectionTitle>
            <TYPE.primary style={{ fontSize: '32px', fontWeight: 'bold', lineHeight: 1 }}>Crypto Pools</TYPE.primary>
            <TYPE.light
              style={{
                fontSize: '20px',
                opacity: 0.6,
                color: '#fff',
                lineHeight: '1.5',
                maxWidth: '736px',
                letterSpacing: '-0.2px',
              }}
            >
              Crypto Pools, also known as liquidity pools, are collective pools of digital assets held by participants
              in the cryptocurrency ecosystem. These pools enable individuals to contribute their cryptocurrencies or
              tokens to a shared liquidity pool, which in turn facilitates various decentralized financial activities.
            </TYPE.light>
          </SectionTitle>

          <TVLDisplay>
            <TYPE.light style={{ fontSize: '14px', lineHeight: '1.4' }}>Total Value locked</TYPE.light>
            {Boolean(showCrypto) ? (
              <TYPE.primary style={{ fontSize: '48px', fontWeight: 'bold', lineHeight: 1 }}>
                ${formatAmount(+totalValueLockedCrypto.toFixed(0))}
              </TYPE.primary>
            ) : (
              <LoaderThin size={32} />
            )}
          </TVLDisplay>
        </SectionHeader>

        {/* Crypto Pools Grid */}
        {Boolean(showCrypto) && (
          <Grid>
            {Object.keys(visibleCrypto)
              .slice(0, 3)
              .filter(
                (item) => visibleCrypto[item].token1.symbol === 'USDC' || visibleCrypto[item].token1.symbol === 'USDT'
              )
              .map((item, index) => (
                <CryptoCard key={`crypto-pool-${index}`} item={visibleCrypto[item]} allPairs={visibleCrypto} />
              ))}
          </Grid>
        )}

        {!showCrypto && (
          <Row>
            <Column style={{ alignItems: 'center', width: '100%' }}>
              <LocalLoader />
            </Column>
          </Row>
        )}

        {/* Show All Button for Crypto Pools */}
        <BasicLink to={`/pools/crypto`}>
          <ShowAllButton>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g opacity="0.5">
                <path
                  d="M6.66666 5H16.6667"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.33334 5.00834L3.34168 4.99908"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.33334 10.0083L3.34168 9.99908"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.33334 15.0083L3.34168 14.9991"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66666 10H16.6667"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66666 15H16.6667"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>

            <TYPE.light style={{ fontSize: '16px', lineHeight: '1.4' }}>Show All</TYPE.light>
          </ShowAllButton>
        </BasicLink>
      </DarkPageContainer>
    </>
  )
}

export default AllPairsPage
